import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, CreditCard, Wallet, ChevronRight, ChevronLeft, Shield, Star, Quote, Target, Menu, X, Instagram, Youtube } from 'lucide-react';
import SEO from '../components/SEO';
import SignupNotification from '../components/SignupNotification';
import MediaCarousel from '../components/MediaCarousel';
import PilotStatusWidget from '../components/PilotStatusWidget';
import SocialLinks from '../components/SocialLinks';
import { trackEvent } from '../utils/consent';
import { trackLandingVisit } from '../utils/visitTracker';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TESTIMONIALS = [
  {
    name: "Sarah Thompson",
    career: "HR Manager",
    quote: "I joined because I was constantly being caught off guard by subscriptions I'd completely forgotten about. Within 10 minutes of using My Direct Debits Reminder, I had everything in one place. It's so simple to use, and I've already cancelled two things I didn't need. Honestly, it's saved me money and stress. I've already recommended it to my team."
  },
  {
    name: "Jason Patel",
    career: "Self-Employed Electrician",
    quote: "I don't usually write reviews, but this app is spot on. I joined because I needed a better way to keep track of my monthly outgoings. It's ridiculously easy to set up, and now I can actually see what's coming out before it happens. It's helped me plan better and avoid surprises. I've told a few mates about it already."
  },
  {
    name: "Chloe Bennett",
    career: "Marketing Executive",
    quote: "A friend recommended it to me and I'm so glad they did. I had no idea how many subscriptions I had until I put them all in. It's clean, easy, and doesn't overcomplicate anything. I've already saved money just by being more aware. Definitely something I'll keep using and recommending."
  },
  {
    name: "Michael O'Connor",
    career: "Delivery Driver",
    quote: "I joined because money was going out of my account and I didn't always know what it was for. This app changed that straight away. It's simple, no bank details needed, and it does exactly what it says. I feel way more in control now. I've shared it with my brother as well."
  },
  {
    name: "Aisha Khan",
    career: "Small Business Owner",
    quote: "As a business owner, I've got personal and business subscriptions flying around everywhere. This app helped me organise everything quickly. It's straightforward, no nonsense, and actually useful. I've already cut down on a few unnecessary payments. I recommend it to anyone who wants clarity."
  },
  {
    name: "Daniel Reeves",
    career: "IT Support Technician",
    quote: "I joined out of curiosity, but it's now something I use regularly. The setup was quick, and I like that I don't have to connect my bank. It's helped me track recurring costs I used to ignore. It's one of those tools you don't realise you need until you use it."
  },
  {
    name: "Lauren Davies",
    career: "Teaching Assistant",
    quote: "I've always struggled to keep on top of my bills, especially with different dates each month. This app makes it really easy to see everything at a glance. I feel more organised and less anxious about money. I've already recommended it to a few friends."
  },
  {
    name: "Ryan Mitchell",
    career: "Gym Manager",
    quote: "I joined because I wanted a clearer picture of my monthly spending. It took minutes to set up, and now I know exactly what's going out and when. It's helped me cut back on things I wasn't even using. Simple idea, but very effective. I'd definitely recommend it."
  }
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/jb80h3j5_Get%20My%20Direct%20debit%20reminder%20app%2015.png";
const BOTTOM_IMAGE = "https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/zv71j0z4_My%20Direct%20debit%20app%20logo.jpg";

const LANDING_CAROUSEL_ITEMS = [
  { type: 'video', poster: '/images/carousel/v7.jpg', src: '/videos/carousel/v7.mp4', title: 'My Direct Debits Reminder App — Advert' },
  { type: 'youtube', poster: '/images/carousel/yt4.jpg', youtubeId: 'QzlB-d6gmF4', title: 'My Direct Debits — Quick walkthrough' },
  { type: 'youtube', poster: '/images/carousel/yt1.jpg', youtubeId: 'yRv3DqQlnHk', title: 'Testimonial — Real user review' },
  { type: 'youtube', poster: '/images/carousel/yt2.jpg', youtubeId: 'cpkXfhyRSCw', title: 'My Direct Debits — Reminder app demo' },
  { type: 'youtube', poster: 'https://i.ytimg.com/vi/ngA5PtUkYpM/hqdefault.jpg', youtubeId: 'ngA5PtUkYpM', title: 'Supporting Dementia Carers with bill reminders' },
  { type: 'video', poster: '/images/carousel/v6.jpg', src: '/videos/carousel/v6.mp4', title: 'Get real-time updates about your bills' },
  { type: 'video', poster: '/images/carousel/v1.jpg', src: '/videos/carousel/v1.mp4', title: 'Stop Money Disappearing Mid-Month' },
  { type: 'video', poster: '/images/carousel/v5.jpg', src: '/videos/carousel/v5.mp4', title: 'Never Miss a Payment Again — App Review' },
  { type: 'video', poster: '/images/carousel/v8.jpg', src: '/videos/carousel/v8.mp4', title: 'I Found 3 Hidden Subscriptions Draining My Account' },
  { type: 'video', poster: '/images/carousel/v4.jpg', src: '/videos/carousel/v4.mp4', title: 'How This App Stopped Money Disappearing on Payday' },
  { type: 'video', poster: '/images/carousel/v2.jpg', src: '/videos/carousel/v2.mp4', title: 'How I Took Control of My Finances' },
  { type: 'video', poster: '/images/carousel/v3.jpg', src: '/videos/carousel/v3.mp4', title: "I Didn't Know Where My Money Was Going" },
  { type: 'image', poster: '/images/carousel/i3.jpg', src: '/images/carousel/full/i3.jpg', title: 'Your bank tells you AFTER. We tell you BEFORE.' },
  { type: 'image', poster: '/images/carousel/i4.jpg', src: '/images/carousel/full/i4.jpg', title: 'Stay in control of your money' },
  { type: 'youtube', poster: '/images/carousel/yt3.jpg', youtubeId: 'nSWxD2gbJBs', title: 'Need help remembering all those bill dates?' },
  { type: 'image', poster: '/images/carousel/i6.jpg', src: '/images/carousel/full/i6.jpg', title: 'Keep forgetting your bill dates?' },
  { type: 'image', poster: '/images/carousel/i7.jpg', src: '/images/carousel/full/i7.jpg', title: 'Looking for a simple bills reminder?' },
  { type: 'image', poster: '/images/carousel/i5.jpg', src: '/images/carousel/full/i5.jpg', title: 'Still forgetting your bill dates?' },
  { type: 'image', poster: '/images/carousel/i1.jpg', src: '/images/carousel/full/i1.jpg', title: 'Your bank tells you AFTER. We tell you BEFORE.' },
  { type: 'image', poster: '/images/carousel/i2.jpg', src: '/images/carousel/full/i2.jpg', title: 'My Direct Debits — See everything. Stay in control.' },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [source, setSource] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const [allTestimonials, setAllTestimonials] = useState(TESTIMONIALS);

  // Fetch user-submitted reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/reviews`);
        if (data && data.length > 0) {
          const userReviews = data.map(r => ({
            name: r.display_name || 'Anonymous',
            career: r.career || '',
            quote: r.review_text,
            rating: r.rating,
            isUserReview: true
          }));
          setAllTestimonials([...TESTIMONIALS, ...userReviews]);
        }
      } catch {
        // Keep hardcoded testimonials only
      }
    };
    fetchReviews();
  }, []);

  // Capture source parameter on first visit
  useEffect(() => {
    trackLandingVisit('/');
    const sourceParam = searchParams.get('source');
    if (sourceParam) {
      // Store source in localStorage for persistence through signup
      localStorage.setItem('mdd_source', sourceParam);
      localStorage.setItem('mdd_source_url', window.location.href);
      localStorage.setItem('mdd_source_timestamp', new Date().toISOString());
      setSource(sourceParam);
    } else {
      // Check if source was stored previously
      const storedSource = localStorage.getItem('mdd_source');
      if (storedSource) {
        setSource(storedSource);
      }
    }
  }, [searchParams]);

  // Auto-advance testimonials
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveTestimonial(prev => (prev + 1) % allTestimonials.length);
      }, 6000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused, allTestimonials.length]);

  const goToTestimonial = useCallback((index) => {
    setActiveTestimonial(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  }, []);

  const nextTestimonial = useCallback(() => {
    goToTestimonial((activeTestimonial + 1) % allTestimonials.length);
  }, [activeTestimonial, goToTestimonial, allTestimonials.length]);

  const prevTestimonial = useCallback(() => {
    goToTestimonial((activeTestimonial - 1 + allTestimonials.length) % allTestimonials.length);
  }, [activeTestimonial, goToTestimonial, allTestimonials.length]);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      try { trackEvent('Lead', { source: 'landing_get_started' }); } catch { /* ignore */ }
      navigate('/auth');
    }
  };

  // Format source name for display
  const formatSourceName = (s) => {
    if (!s) return '';
    return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-page">
      <SEO 
        title="My Direct Debits – Direct Debit Reminder App | Track Your Payments"
        description="Never get caught off guard by your direct debits again. Track all your recurring payments, get smart reminders, and stay in control of your finances. Free to start."
        path="/"
      />
      
      {/* Recommended by banner */}
      {source && (
        <div className="bg-primary-light border-b border-primary/20 px-4 py-2">
          <p className="text-center text-sm text-primary-dark">
            Recommended by <span className="font-semibold">{formatSourceName(source)}</span>
          </p>
        </div>
      )}
      
      {/* Header */}
      <header className="glass-header px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-10 w-10 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-dark text-lg leading-tight">My Direct Debits</h1>
              <p className="text-xs text-text-light">Reminder App</p>
            </div>
          </div>
          {user ? (
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
              <a href="https://www.qualiquiz.app/quiz/find-out-in-60-seconds-which-money-management-style-are-you-" target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex bg-gold text-white font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-gold/80 transition-colors shadow-sm" data-testid="quiz-header-link">Which Spender Am I? Quiz</a>
              <Link to="/how-it-works" className="hidden sm:inline-flex bg-primary-light text-primary font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-primary hover:text-white transition-colors" data-testid="how-it-works-header-link">How It Works</Link>
              <Link to="/blog" className="hidden sm:inline-flex bg-primary-light text-primary font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-primary hover:text-white transition-colors">Blog</Link>
              <Link
                to="/dashboard"
                data-testid="go-to-dashboard-btn"
                className="btn-primary px-5 py-2 text-sm"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                data-testid="mobile-menu-open-btn"
                className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-light text-primary hover:bg-primary hover:text-white transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
              <a href="https://www.qualiquiz.app/quiz/find-out-in-60-seconds-which-money-management-style-are-you-" target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex bg-gold text-white font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-gold/80 transition-colors shadow-sm" data-testid="quiz-header-link">Which Spender Am I? Quiz</a>
              <Link to="/how-it-works" className="hidden sm:inline-flex bg-primary-light text-primary font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-primary hover:text-white transition-colors" data-testid="how-it-works-header-link">How It Works</Link>
              <Link to="/blog" className="hidden sm:inline-flex bg-primary-light text-primary font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-primary hover:text-white transition-colors" data-testid="blog-header-link">Blog</Link>
              <Link
                to="/auth"
                data-testid="sign-in-header-btn"
                className="hidden sm:inline-flex text-primary font-medium hover:text-primary-hover transition-colors"
              >
                Sign In
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                data-testid="mobile-menu-open-btn"
                className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-light text-primary hover:bg-primary hover:text-white transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[80] sm:hidden bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 right-0 left-0 bg-white shadow-xl rounded-b-2xl"
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
              <span className="font-heading font-bold text-dark">Menu</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
                className="w-10 h-10 rounded-full hover:bg-border/40 flex items-center justify-center text-text-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col py-2">
              <a
                href="https://www.qualiquiz.app/quiz/find-out-in-60-seconds-which-money-management-style-are-you-"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-quiz"
                className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3"
              >
                <span className="w-2 h-2 rounded-full bg-gold" /> Which Spender Am I? Quiz
              </a>
              <Link
                to="/how-it-works"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-how"
                className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3"
              >
                <span className="w-2 h-2 rounded-full bg-primary" /> How It Works
              </Link>
              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-blog"
                className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3"
              >
                <span className="w-2 h-2 rounded-full bg-primary" /> Blog
              </Link>
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className="px-5 py-3 text-primary font-semibold hover:bg-primary-light transition-colors flex items-center gap-3 border-t border-border"
                >
                  <span className="w-2 h-2 rounded-full bg-primary" /> Go to Dashboard
                </Link>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-signin"
                  className="px-5 py-3 text-primary font-semibold hover:bg-primary-light transition-colors flex items-center gap-3 border-t border-border"
                >
                  <span className="w-2 h-2 rounded-full bg-primary" /> Sign In
                </Link>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="px-4 py-12 md:py-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <h2 className="font-heading font-bold text-dark text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight mb-6">
              Never get caught off guard by your Direct Debits again
            </h2>
            <p className="text-text-secondary text-lg md:text-xl mb-5 leading-relaxed">
              Know exactly what's leaving your account, before it happens.
            </p>

            <Link
              to="/auth"
              data-testid="pilot-banner-link"
              aria-label="Join the pilot programme — get your free report"
              className="block mb-3 rounded-2xl overflow-hidden shadow-md border border-border hover:shadow-lg transition-shadow"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/k4flsky9_Inviting%2010%20people%20to%20join%20pilot%20mddreminder%20.png"
                alt="We're inviting 10 people to join our pilot programme — Get your free report"
                className="block w-full h-auto"
                loading="eager"
              />
            </Link>

            <PilotStatusWidget />

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="flex items-center gap-2 bg-primary-light text-primary-dark px-4 py-2 rounded-full text-sm font-medium">
                <Bell className="w-4 h-4" />
                Smart reminders
              </div>
              <div className="flex items-center gap-2 bg-primary-light text-primary-dark px-4 py-2 rounded-full text-sm font-medium">
                <CreditCard className="w-4 h-4" />
                Track payments
              </div>
              <div className="flex items-center gap-2 bg-primary-light text-primary-dark px-4 py-2 rounded-full text-sm font-medium">
                <Wallet className="w-4 h-4" />
                Stay in control
              </div>
            </div>

            <button
              onClick={handleGetStarted}
              data-testid="get-started-btn"
              className="btn-primary px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl"
            >
              Join the free pilot
            </button>
            <p className="text-text-light text-sm mt-4">
              Free to start. No card details required. No ads.
            </p>
            <div className="mt-5" data-testid="hero-social-links">
              <p className="text-text-light text-sm mb-3 font-medium">Follow &amp; subscribe for tips, demos and updates:</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href="https://www.instagram.com/mddreminder"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow My Direct Debits on Instagram"
                  data-testid="hero-instagram-link"
                  style={{ background: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 40%, #8134AF 70%, #515BD4 100%)' }}
                  className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-xl hover:scale-105 transform transition-all duration-200"
                >
                  <Instagram className="w-5 h-5" />
                  <span>Follow on Instagram</span>
                </a>
                <a
                  href="https://www.youtube.com/@mydirectdebitreminderapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Subscribe to My Direct Debits on YouTube"
                  data-testid="hero-youtube-link"
                  style={{ backgroundColor: '#FF0000' }}
                  className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-xl hover:scale-105 hover:brightness-110 transform transition-all duration-200"
                >
                  <Youtube className="w-5 h-5" fill="white" />
                  <span>Subscribe on YouTube</span>
                </a>
              </div>
            </div>
          </div>

          <div className="animate-slide-up">
            <div className="w-full max-w-[280px] md:max-w-md mx-auto rounded-2xl shadow-lg overflow-hidden">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                poster="/videos/hero/welcome-poster.jpg"
                className="w-full rounded-2xl"
                data-testid="hero-video"
              >
                <source src="/videos/hero/welcome.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="px-4 py-16 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-10">
            <div className="animate-fade-in">
              <h3 className="font-heading font-bold text-dark text-2xl mb-4">
                What is My Direct Debits?
              </h3>
              <p className="text-text-secondary leading-relaxed">
                My Direct Debits is a powerful yet simple app designed to help you track and manage all your recurring payments. 
                Whether it's your monthly subscriptions, utility bills, or insurance payments, our app ensures you always know 
                what's coming out of your account and when. No more unexpected charges or overdraft surprises — just complete 
                visibility and control over your finances.
              </p>
            </div>

            <div className="animate-fade-in" data-testid="landing-who-video">
              <div className="relative w-full max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-lg border border-border bg-black mb-8" style={{ paddingTop: '56.25%' }}>
                <iframe
                  src="https://www.youtube.com/embed/ZVlZ4aOenGc?rel=0"
                  title="How My Direct Debits Works"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                />
              </div>
            </div>

            <div className="animate-fade-in">
              <h3 className="font-heading font-bold text-dark text-2xl mb-4">
                Who is it for?
              </h3>
              <p className="text-text-secondary leading-relaxed">
                My Direct Debits reminder is perfect for anyone who wants to take control of their recurring payments. Whether you're a 
                young professional managing your first bills, a busy parent juggling multiple household expenses, running a small business 
                or someone looking to get a better handle on your subscriptions and memberships — our app is built for you. If you've ever 
                been caught off guard by a direct debit, this app is the solution you've been looking for.
              </p>
            </div>

            {/* Before vs After Promo Image */}
            <div className="animate-fade-in" data-testid="promo-image-section">
              <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-lg border border-border/50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/qzbmewos_Get%20My%20Direct%20debit%20reminder%20app%2015.png"
                  alt="Your bank tells you AFTER — We tell you BEFORE. Download My Direct Debits Reminder App Free"
                  className="w-full"
                  data-testid="promo-image"
                />
              </div>
            </div>

            <div className="animate-fade-in">
              <h3 className="font-heading font-bold text-dark text-2xl mb-4">
                Why use My Direct Debit Reminder App?
              </h3>
              <ul className="space-y-3">
                {[
                  "See all your direct debits in one place — no more hunting through bank statements",
                  "Get smart reminders before payments leave your account",
                  "Easily track payment dates and amounts at a glance",
                  "Identify and cancel unwanted subscriptions to save money",
                  "Never miss a payment or face unexpected overdraft fees",
                  "Free to start with an option to upgrade for unlimited tracking"
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3 text-text-secondary">
                    <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Security & Trust */}
            <div className="animate-fade-in bg-primary-light rounded-2xl p-6 mt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-dark text-lg mb-2">
                    Security & Trust
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    My Direct Debits Reminder App only collects the information needed to create and manage your account, such as your name, email address, and the payment reminders you choose to enter.
                  </p>
                  <p className="text-text-secondary text-sm leading-relaxed mt-2">
                    <strong className="text-dark">We do not collect or store your bank details, and we do not store card details.</strong> Payments are processed securely by Stripe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Swamped with Direct Debits Promo */}
      <section className="px-4 py-12 bg-white">
        <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-lg">
          <Link
            to="/auth"
            data-testid="swamped-promo-cta-link"
            className="block group"
            aria-label="End payment surprises — Sign up to take control"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/5fixrm2s_End_Payment_Surprises_Stay_in_Control.jpg"
              alt="Take control of recurring payments together — Stop payment surprises with My Direct Debits"
              className="w-full transition-transform duration-300 group-hover:scale-[1.01]"
              data-testid="swamped-promo-image"
              loading="lazy"
            />
          </Link>
        </div>
      </section>

      {/* Media Carousel — videos & images */}
      <section className="px-4 py-14 md:py-16 bg-white border-t border-border" data-testid="landing-media-carousel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h3 className="font-heading font-bold text-dark text-2xl md:text-3xl mb-2">See MDD in action</h3>
            <p className="text-text-secondary">Real stories. Real reminders. Tap any card to play or expand.</p>
          </div>
          <div className="px-2 sm:px-6">
            <MediaCarousel items={LANDING_CAROUSEL_ITEMS} />
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="px-4 py-16 bg-page" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading font-bold text-dark text-2xl md:text-3xl text-center mb-2">
            What Our Users Say
          </h3>
          <p className="text-text-secondary text-center mb-10">
            Real people, real results
          </p>

          <div className="relative">
            {/* Carousel Card */}
            <div
              className="bg-white rounded-2xl shadow-md border border-border/50 p-8 md:p-10 min-h-[280px] flex flex-col justify-between transition-all duration-500"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              data-testid="testimonial-card"
            >
              <div>
                <Quote className="w-8 h-8 text-primary/20 mb-4" />
                <p className="text-text-secondary leading-relaxed text-base md:text-lg italic" data-testid="testimonial-quote">
                  "{allTestimonials[activeTestimonial].quote}"
                </p>
              </div>

              <div className="flex items-center justify-between mt-6 pt-6 border-t border-border/50">
                <div>
                  <div className="flex gap-0.5 mb-1">
                    {[...Array(allTestimonials[activeTestimonial].rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="font-heading font-semibold text-dark" data-testid="testimonial-name">
                    {allTestimonials[activeTestimonial].name}
                  </p>
                  <p className="text-text-light text-sm" data-testid="testimonial-career">
                    {allTestimonials[activeTestimonial].career}
                  </p>
                </div>

                {/* Nav Arrows */}
                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-text-secondary hover:bg-primary hover:text-white hover:border-primary transition-all"
                    aria-label="Previous testimonial"
                    data-testid="testimonial-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-text-secondary hover:bg-primary hover:text-white hover:border-primary transition-all"
                    aria-label="Next testimonial"
                    data-testid="testimonial-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 mt-6" data-testid="testimonial-dots">
              {allTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === activeTestimonial
                      ? 'w-8 bg-primary'
                      : 'w-2 bg-border hover:bg-text-light'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by 1,200+ Users */}
      <section className="px-4 py-8 bg-white" data-testid="trusted-users-section">
        <div className="max-w-3xl mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/h2onoefp_Mddreminder%20trusted%20by%2012000.png"
            alt="Trusted by 1,200+ users — Know what's leaving your account before it happens"
            className="w-full rounded-2xl shadow-lg"
            data-testid="trusted-users-image"
          />
        </div>
      </section>

      {/* No Warning vs Pre Warning Comparison */}
      <section className="px-4 py-12 bg-white" data-testid="warning-comparison-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 gap-4 md:gap-8">
            <div className="text-center">
              <h3 className="font-heading font-bold text-alert text-xl md:text-2xl mb-3">No Warning</h3>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-border/50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/uduacjc0_file_00000000a8dc724396af00d8fcb37168.png"
                  alt="No warning — sad face with empty MDD dashboard showing £0.00"
                  className="w-full"
                  data-testid="no-warning-image"
                />
              </div>
            </div>
            <div className="text-center">
              <h3 className="font-heading font-bold text-primary text-xl md:text-2xl mb-3">Pre Warning</h3>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-border/50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/wa2u7k3a_file_00000000f94872438d13c4374a361a40.png"
                  alt="Pre warning — happy face with MDD dashboard showing £43.00 and upcoming payments"
                  className="w-full"
                  data-testid="pre-warning-image"
                />
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <button
              onClick={handleGetStarted}
              data-testid="comparison-get-started-btn"
              className="btn-primary px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Before vs After Promo Image */}
      <section className="px-4 py-12 bg-white" data-testid="promo-image-section">
        <div className="max-w-3xl mx-auto">
          <img 
            src={BOTTOM_IMAGE}
            alt="My Direct Debits - Reminder App Logo"
            className="w-full max-w-[200px] md:max-w-xs mx-auto drop-shadow-xl"
            data-testid="promo-comparison-image"
          />
        </div>
      </section>

      {/* Partnership Banner */}
      <section className="px-4 py-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <a
            href="https://www.qualiquiz.app/quiz/is-your-organisation-proactive-or-reactive-in-payment-notifications"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partnership-banner"
            className="block bg-gradient-to-r from-dark to-primary-dark rounded-2xl p-8 text-center text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2">
              Interested in Collaboration or Partnership Opportunity?
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Find out how proactive your organisation is with payment notifications
            </p>
            <span className="inline-block bg-white text-dark font-semibold px-6 py-2.5 rounded-xl text-sm hover:bg-gray-100 transition-colors">
              Take the Assessment
            </span>
          </a>
        </div>
      </section>

      {/* Available Soon Section */}
      <section className="px-4 py-16 bg-page">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-heading font-bold text-dark text-2xl mb-2">
            Available Soon
          </h3>
          <p className="text-text-secondary mb-8">
            Download our mobile app for the best experience
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* Apple App Store Badge */}
            <a 
              href="#" 
              className="transition-transform hover:scale-105"
              aria-label="Download on the App Store"
            >
              <img 
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                alt="Download on the App Store" 
                className="h-12"
              />
            </a>
            {/* Google Play Store Badge */}
            <a 
              href="#" 
              className="transition-transform hover:scale-105"
              aria-label="Get it on Google Play"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                className="h-12"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 bg-dark">
        <div className="max-w-5xl mx-auto text-center">
          <img src={LOGO_URL} alt="My Direct Debits" className="h-12 w-12 mx-auto mb-4 opacity-80" />
          <p className="text-white/60 text-sm mb-4">
            A product by Voliciti Group Ventures Ltd
          </p>
          <div className="flex justify-center gap-6 text-sm mb-4">
            <Link to="/privacy" className="text-white/60 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/60 hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/how-it-works" className="text-white/60 hover:text-white transition-colors">
              How It Works
            </Link>
            <Link to="/blog" className="text-white/60 hover:text-white transition-colors">
              Blog
            </Link>
          </div>
          <div className="mb-4">
            <SocialLinks variant="dark" />
          </div>
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-lg px-4 py-2 inline-flex items-center gap-3">
              <a href="http://www.copyrightregistrationservice.com" target="_blank" rel="noopener noreferrer">
                <img src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/15jyam7f_seal_120.gif" alt="Copyright Registration Service - Registered with the IPRO" className="w-14 h-14" style={{borderStyle:'none'}} />
              </a>
              <div className="text-left" style={{fontFamily:"'Times New Roman', Times, serif", fontSize:'10pt'}}>
                Registered with the <strong>IP Rights Office<br/>Copyright Registration Service</strong><br/>
                Ref: <a href="https://www.copyrightregistrationservice.com/cgi-bin/verify.cgi?crsnum=30718779332" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">30718779332</a>
              </div>
            </div>
          </div>
          <p className="text-white/40 text-xs">
            © 2026 Voliciti Group Ventures Ltd. All rights reserved.
          </p>
        </div>
      </footer>
      <SignupNotification />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Voliciti Group Ventures Ltd",
        "url": "https://mddreminder.uk",
        "description": "My Direct Debits Reminder App — track your recurring payments and get notified before money leaves your account.",
        "contactPoint": { "@type": "ContactPoint", "email": "info@mddreminder.uk", "telephone": "+447367431859", "contactType": "customer service" },
        "sameAs": ["https://www.mddreminder.app", "https://www.instagram.com/mddreminder", "https://www.youtube.com/@mydirectdebitreminderapp"]
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "My Direct Debits",
        "url": "https://mddreminder.uk",
        "description": "Never get caught off guard by your direct debits again. Track payments, get smart reminders, stay in control."
      })}} />
    </div>
  );
}
